'use client';

import { AlarmClock, RotateCcw, UserRound } from 'lucide-react';
import { Banner } from '@/components/ui/banner';
import { useOptionalFeatureFlags } from '@/lib/providers/feature-flags-provider';
import {
  EXTERNAL_HANDLING_FEATURE_FLAG,
  isHandoverOverdue,
  type Handling
} from '@/lib/services/matter-handling';
import { HandedOverDetail, handedOverTitle } from './handed-over-summary';
import { useInstitutionHandling } from './use-institution-handling';
import { useInventoryItemHandling } from './use-inventory-item-handling';

/**
 * What the inventory item drawer shows when the work on this asset is being done
 * outside the firm (VALE-1854).
 *
 * **The drawer used to say this in a chip and a hover.** The handover was a badge
 * beside the status pill; the note and the expected-by date were reachable only by
 * hovering it, and the gestures that change either were folded into the header's
 * `⋯` along with *Change type* and *Delete*. So the three facts a reader most
 * needs — who has it, what they are doing, when it is due back — were the three
 * least visible things on a drawer with room to spare. Reported by the first
 * person to drive the feature without having read the code, twice in a row:
 * *"buried here"* of the actions, *"is this surfaced anywhere??"* of the note.
 *
 * **A `<Banner>`, not a hand-rolled callout.** It is the standard component for a
 * notice on a tone ladder, and the registry's rule is that anything which LOOKS
 * like one must BE one, down to base elements
 * (`frontend/.claude/docs/standardised-components.md`). A bespoke bordered `div`
 * was written first here and was wrong: it re-derived the chip, the tone tints and
 * the action layout that `Banner` already owns, and would have drifted from every
 * other notice in the product the first time the banner tokens moved.
 *
 * **A panel beside the record, not one that replaces it.** This is the deliberate
 * difference from {@link InstitutionHandedOverPanel}, which stands in for the
 * letter composer entirely so that no Send button exists in its subtree. Nothing
 * on an item drawer writes to a third party, so there is no refusal to make
 * unreachable here and no reason to withhold the asset's own details — the firm
 * still needs to know what the asset IS, and arguably needs it more once somebody
 * else is realising it.
 *
 * **First in the body, ahead of the record.** VALE-863 moved the workflow blocks
 * BELOW `AttributeSections` on the rule that a drawer should say what the asset is
 * before what you do to it. This is not one of those: it is not an action on the
 * asset but a statement about whose work it is, and it changes how everything
 * under it should be read — the values below are being maintained by somebody
 * outside the firm.
 */
export function ItemHandedOverPanel({
  matterId,
  inventoryItemId,
  itemLabel,
  handling,
  canSetOwnHandling,
  institutionId,
  institutionName
}: {
  matterId: number;
  inventoryItemId: number;
  itemLabel: string;
  handling: Handling | null | undefined;
  canSetOwnHandling: boolean;
  /**
   * The institution this handover was inherited FROM, where there is one
   * (VALE-1854). Together these are what let the banner act on an inherited
   * handover rather than describing one it cannot touch.
   */
  institutionId?: number | null;
  institutionName?: string | null;
}) {
  const { hasFeature } = useOptionalFeatureFlags();
  const externalHandlingEnabled = hasFeature(EXTERNAL_HANDLING_FEATURE_FLAG);

  /*
    **Both hooks, unconditionally, and one of them chosen below.** An item's own
    handover writes to the item endpoint; an INHERITED one belongs to the
    institution and has to write to the institution endpoint. Rules of hooks mean
    neither can be called behind a branch, and each is inert until its action
    fires, so calling both costs a render and nothing else.
  */
  const item = useInventoryItemHandling({
    matterId,
    inventoryItemId,
    itemLabel,
    handling,
    canSetOwnHandling
  });
  const institution = useInstitutionHandling({
    matterId,
    institutionId: institutionId ?? 0,
    institutionName: institutionName ?? '',
    handling
  });

  const applies = item.applies;
  /*
    **Take-back is offered from HERE, whichever grain owns the handover.**

    The first attempt made the inherited case read-only and sent the reader to
    Discovery with an "Open in Discovery" link. Driven end to end that is: see the
    handover on the asset, click through to Discovery, scan a list of seventeen
    institutions and a dozen properties for the right one, open it, and only then
    take back. Four steps and a manual search to undo one thing the reader was
    already looking at.

    Ownership was never the problem — DISCLOSURE was. Taking back an inherited
    handover writes to the institution and therefore covers every asset under it,
    which the reader must know before they click, not be protected from by having
    the control removed. So the control stays here and the line below says what it
    will cover.
  */
  const acting = applies ? item : institution;
  const canAct = applies || institutionId != null;
  const { isPending, openDialog, takeBack, dialog } = acting;

  /*
    **Shown for an inherited handover too, and read-only when it is** (VALE-1854).

    `applies` is false for an institution-backed item, because that item cannot
    carry a handler of its own — a DB CHECK sees to it. Gating the whole panel on
    it meant an asset at a handed-over institution showed NOTHING about the
    handover: the drawer read `item.handling`, which is null for exactly those
    rows, so the one surface the ticket names as "must show, must not be
    changeable" showed nothing and offered its ordinary workflow actions. Caught
    by opening a Commonwealth Bank account whose institution had been handed to
    the executor.

    So the panel gates on the flag and a handler, and `applies` decides only
    whether the ACTIONS are offered. Read-only is the correct treatment for an
    inherited handover rather than a limitation: it is the institution's handover,
    and it is taken back from the institution.
  */
  if (!externalHandlingEnabled || !handling?.handledBy) return null;

  const overdue = isHandoverOverdue(handling);

  return (
    /*
      The testid sits on a wrapper, not on `Banner`: it takes no `data-*` and
      spreads no rest props, so one passed to it is silently dropped — a test
      hook that compiles, renders nothing, and fails only later as a confusing
      "element not found".
    */
    <div className="mb-4" data-testid="item-handed-over-panel">
      <Banner
        /*
          `caution` when late, `neutral` otherwise — never `critical`. A late
          handover is something to look at, not a failure, which is the same call
          EH-010 makes for the badge's ramps. The icon is overridden to match the
          badge's person/clock pair rather than taking the tone defaults (Info,
          AlertTriangle), so a reader who has learnt the chip does not meet a
          second vocabulary here.
        */
        tone={overdue ? 'caution' : 'neutral'}
        icon={overdue ? AlarmClock : UserRound}
        title={handedOverTitle(handling)}
        line={
          <>
            <HandedOverDetail handling={handling} />
            {/*
              **The blast radius, stated before the click rather than discovered
              after it.** An inherited handover is the institution's, so taking it
              back here releases every asset under that institution — five
              Commonwealth Bank accounts, not the one on screen. That is the fact
              the reader needs in order to use the button safely, and it is what
              earns the button its place here instead of a trip to Discovery.
            */}
            {!applies && institutionName ? (
              <span>
                {' '}
                &middot; Handed over at {institutionName}, so taking it back
                covers every asset there.
              </span>
            ) : null}
          </>
        }
        action={
          canAct
            ? {
                label: 'Take back into Vale',
                onClick: takeBack,
                icon: RotateCcw,
                disabled: isPending
              }
            : undefined
        }
        secondaryAction={
          canAct
            ? {
                label: 'Edit',
                onClick: openDialog,
                variant: 'link',
                disabled: isPending
              }
            : undefined
        }
      />
      {canAct ? dialog : null}
    </div>
  );
}
